import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { getPushNotificationsApi } from 'api/notifications';
import { useAuth } from 'contexts/AuthContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import type { GetPushNotificationResponseDTO } from 'declarations/dto/notifications';
import { NotificationsPermissionsE } from 'declarations/enums/notifications';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { RoutePathsE } from 'declarations/enums/routes';
import { StorageE } from 'declarations/enums/storage';

const usePushNotifications = (): void => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { isUserLoggedIn } = useAuth();
  const { userProfile } = useUserProfile();
  const navigate = useNavigate();
  const socketRef = useRef<WebSocket | null>(null);
  const notificationRef = useRef<Notification | null>(null);

  /**
   * Handlers
   */
  const handleNotificationClick = useCallback(() => {
    if (window.location.pathname !== RoutePathsE.TICKETS_PAGE) {
      navigate(RoutePathsE.TICKETS_PAGE);
    }

    window.focus();
  }, [navigate]);
  const displayNotification = useCallback(
    ({ action, sender }: GetPushNotificationResponseDTO) => {
      const notificationOptions = {
        body: action,
      };

      notificationRef.current = new Notification(sender.displayName, notificationOptions);
      notificationRef.current.addEventListener('click', handleNotificationClick);
    },
    [handleNotificationClick]
  );
  const handleSocketGetMessage = useCallback(
    (event: MessageEvent) => {
      const notification = JSON.parse(event.data);
      displayNotification(notification);

      if ((notification as GetPushNotificationResponseDTO).actionTypeId === 'MessageBoardUpdated') {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchBannerMessages]);
      } else {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchNotificationsHistory]);
      }
    },
    [displayNotification, queryClient]
  );
  const setupSocket = useCallback(() => {
    const idToken = localStorage.getItem(StorageE.IdToken);

    if (userProfile && idToken) {
      const { id: userId } = userProfile;

      socketRef.current = getPushNotificationsApi({ idToken, userId });
      socketRef.current.addEventListener('message', handleSocketGetMessage);
    }
  }, [userProfile, handleSocketGetMessage]);
  const askNotificationsPermission = useCallback(async () => {
    const permission = await Notification.requestPermission();

    if (permission === NotificationsPermissionsE.Granted) {
      setupSocket();
    }
  }, [setupSocket]);
  const setupNotifications = useCallback(() => {
    const isNotificationsSupported = 'Notification' in window;

    if (isNotificationsSupported) {
      Notification.permission === NotificationsPermissionsE.Granted
        ? setupSocket()
        : askNotificationsPermission();
    }
  }, [askNotificationsPermission, setupSocket]);

  /**
   * Effects
   */
  useEffect(() => {
    if (isUserLoggedIn && userProfile) {
      setupNotifications();
    }

    return () => {
      if (notificationRef.current && socketRef.current) {
        notificationRef.current.removeEventListener('click', handleNotificationClick);
        socketRef.current.removeEventListener('message', handleSocketGetMessage);
        socketRef.current.close();
      }
    };
  }, [
    isUserLoggedIn,
    userProfile,
    setupNotifications,
    handleSocketGetMessage,
    handleNotificationClick,
  ]);
};

export default usePushNotifications;
